import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Mail, Search } from "lucide-react";
import { EmptyState, ErrorBanner, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { MessageInsights } from "../../components/MessageInsights";

const STATUSES = ["all", "queued", "retrying", "accepted", "delivered", "bounced", "complained", "failed", "simulated", "held"];

function Engagement({ m }) {
  const tr = m.tracking || {};
  const e = m.engagement || {};
  const cells = [["Opens", tr.opens ? e.opens || 0 : "Off", "message-opens"], ["Clicks", tr.clicks ? e.clicks || 0 : "Off", "message-clicks"]];
  return (
    <div data-testid="message-engagement">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Engagement</div>
      <div className="grid grid-cols-2 gap-3">
        {cells.map(([k, v, id]) => (
          <div key={k} className="rounded-md border border-white/10 bg-black/30 p-3"><div className="text-xs text-zinc-500">{k}</div><div className="font-display text-xl text-white" data-testid={id}>{v}</div></div>
        ))}
      </div>
      {e.clicked_urls?.length > 0 && <ul className="mt-2 space-y-1">{e.clicked_urls.map((u) => <li key={u} className="break-all font-mono text-[11px] text-zinc-400">{u}</li>)}</ul>}
      {(tr.opens || tr.clicks) && <p className="mt-2 text-xs text-zinc-500">Opens are approximate: some mail apps load or block images automatically.</p>}
    </div>
  );
}

function Detail({ id, onClose }) {
  const { t } = useT();
  const { data, error, loading } = useLoad(() => (id ? api.get(`/app/messages/${id}`).then((r) => r.data) : Promise.resolve(null)), [id]);
  useEffect(() => {
    // The phone's back gesture/button closes the detail instead of leaving the page.
    if (!id) return undefined;
    window.history.pushState({ cario_sheet: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (window.history.state?.cario_sheet) window.history.back();
    };
  }, [id, onClose]);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#0F0E13] sm:max-w-xl" data-testid="message-detail">
        <button type="button" onClick={onClose} className="mb-3 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white sm:hidden" data-testid="message-detail-back"><ChevronLeft className="h-4 w-4" />{t("nav.messages")}</button>
        <SheetHeader><SheetTitle className="pr-12 text-left">{data?.subject || "Message"}</SheetTitle></SheetHeader>
        <ErrorBanner error={error} />
        {loading ? <Spinner /> : data && (
          <div className="mt-6 space-y-6 text-sm">
            <div className="flex items-center gap-2"><StatusBadge status={data.status} testId="message-detail-status" />{data.test_mode && <span className="text-xs text-fuchsia-300">{t("Test mode \u2014 no email was sent")}</span>}</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              {[["ID", data.id], [t("From"), data.from], [t("To"), data.to.join(", ")], ["Cc", data.cc?.join(", ") || "—"], ["Bcc", data.bcc?.length ? `${data.bcc.length} ${t("recipient(s)")}` : "—"],
                [t("Suppressed"), data.suppressed_recipients?.join(", ") || "—"], [t("Source"), data.source || "—"], [t("Attempts"), data.attempts ?? "—"], [t("Created"), fmt(data.created_at)], [data.test_mode ? t("Simulated") : t("m.accepted"), fmt(data.accepted_at)]].map(([k, v]) => (
                <div key={k} className="contents"><dt className="text-zinc-500">{k}</dt><dd className="break-all font-mono text-xs text-zinc-200">{v}</dd></div>
              ))}
            </dl>
            {data.provider && (
              <div><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("Provider response")}</div>
                <pre className="whitespace-pre-wrap rounded-md border border-white/10 bg-black/40 p-3 font-mono text-xs text-zinc-300" data-testid="message-provider-response">{data.provider.code ?? "—"} {data.provider.response}</pre></div>
            )}
            <Engagement m={data} />
            <MessageInsights m={data} />
            {data.error && <div className="rounded-md border border-red-500/25 bg-red-500/5 p-3 text-xs text-red-200" data-testid="message-error">{data.error}</div>}
            <div><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("Events")}</div>
              <ol className="space-y-2 border-l border-white/10 pl-4">
                {data.events.map((e) => <li key={`${e.type}-${e.at}`} className="text-xs"><span className="font-mono text-purple-200">{e.type}</span> <span className="text-zinc-500">· {fmt(e.at)}</span>{e.detail && typeof e.detail === "string" && <div className="text-zinc-400">{e.detail}</div>}</li>)}
              </ol></div>
            {data.status === "accepted" && <p className="text-xs text-zinc-500">Accepted by the SMTP server. Final delivery is unknown unless Amazon SES delivery events are configured.</p>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Messages() {
  const { t } = useT();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const { data, error, loading } = useLoad(() => api.get("/app/messages", { params: { status: status === "all" ? undefined : status, q: query || undefined } }).then((r) => r.data), [status, query]);
  return (
    <div>
      <PageHeader title={t("nav.messages")} description={t("desc.messages")} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); setQuery(q.trim()); }}>
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search subject, recipient or message ID")} className="pl-9" aria-label="Search messages" data-testid="messages-search" />
        </form>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-48" data-testid="messages-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (data.length === 0 ? (
        <EmptyState icon={Mail} title={t("No messages found")} body={t("Send your first email with the REST API or a template test send.")} testId="messages-empty" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="messages-table">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">{t("Status")}</th><th className="p-3">{t("tpl.subject")}</th><th className="p-3">{t("To")}</th><th className="p-3">{t("From")}</th><th className="p-3">{t("Created")}</th></tr></thead>
            <tbody>{data.map((m) => (
              <tr key={m.id} onClick={() => setOpen(m.id)} className="cursor-pointer border-t border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`message-row-${m.id}`}>
                <td className="p-3"><StatusBadge status={m.status} /></td>
                <td className="max-w-[240px] truncate p-3 text-zinc-200">{m.subject}</td>
                <td className="max-w-[200px] truncate p-3 font-mono text-xs text-zinc-400">{m.to.join(", ")}</td>
                <td className="max-w-[180px] truncate p-3 font-mono text-xs text-zinc-400">{m.from}</td>
                <td className="whitespace-nowrap p-3 text-xs text-zinc-500">{fmt(m.created_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ))}
      <Detail id={open} onClose={close} />
    </div>
  );
}
